import { useCallback, useEffect, useRef } from 'react';
import { useReactFlow } from 'reactflow';
import {
  DEFAULT_HEIGHT,
  DEFAULT_WIDTH,
} from '../../components/nodeTypes/constants';

const useFitSelectableLeafActionsIntoView = (): ((
  leafActionIds: Array<string>,
) => void) => {
  const didFitSelectableActionsKeyRef = useRef<string | null>(null);
  const pendingFitSelectableActionsKeyRef = useRef<string | null>(null);
  const isMountedRef = useRef(true);
  const { fitBounds, getNodes } = useReactFlow();

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return useCallback(
    (leafActionIds: Array<string>) => {
      if (leafActionIds.length === 0) return;

      const key = [...leafActionIds]
        .sort((a, b) => a.localeCompare(b))
        .join('|');

      if (didFitSelectableActionsKeyRef.current === key) return;
      if (pendingFitSelectableActionsKeyRef.current === key) return;

      pendingFitSelectableActionsKeyRef.current = key;

      const LEFT_PADDING = 300;
      const MAX_FRAMES = 90; // ~1.5s at 60fps
      let frames = 0;

      const tick = () => {
        if (!isMountedRef.current) return;

        frames += 1;

        const lowestActionIds = new Set(leafActionIds);
        const selectableNodes = getNodes().filter(n =>
          lowestActionIds.has(n.id),
        );

        if (selectableNodes.length === 0) {
          if (frames < MAX_FRAMES && typeof window !== 'undefined') {
            window.requestAnimationFrame(tick);
          } else {
            pendingFitSelectableActionsKeyRef.current = null;
          }
          return;
        }

        // Wait for node sizes so the bounds are correct.
        const nodesNotMeasuredYet = selectableNodes.some(
          n => n.width === undefined || n.height === undefined,
        );
        if (nodesNotMeasuredYet) {
          if (frames < MAX_FRAMES && typeof window !== 'undefined') {
            window.requestAnimationFrame(tick);
          } else {
            pendingFitSelectableActionsKeyRef.current = null;
          }
          return;
        }

        let minX = Infinity;
        let minY = Infinity;
        let maxX = -Infinity;
        let maxY = -Infinity;

        for (const node of selectableNodes) {
          const position = node.positionAbsolute ?? node.position;
          const width = node.width ?? DEFAULT_WIDTH;
          const height = node.height ?? DEFAULT_HEIGHT;

          minX = Math.min(minX, position.x);
          minY = Math.min(minY, position.y);
          maxX = Math.max(maxX, position.x + width);
          maxY = Math.max(maxY, position.y + height);
        }

        const bounds = {
          x: minX,
          y: minY,
          width: maxX - minX,
          height: maxY - minY,
        };

        fitBounds(
          {
            x: bounds.x - LEFT_PADDING,
            y: bounds.y - DEFAULT_HEIGHT,
            width: bounds.width + LEFT_PADDING,
            height: bounds.height + DEFAULT_HEIGHT * 2,
          },
          { duration: 1000, padding: 0.6 },
        );

        didFitSelectableActionsKeyRef.current = key;
        pendingFitSelectableActionsKeyRef.current = null;
      };

      if (typeof window !== 'undefined') {
        window.requestAnimationFrame(tick);
      }
    },
    [fitBounds, getNodes],
  );
};

export default useFitSelectableLeafActionsIntoView;
